/* Generate a single "Consultant Handbook" PDF containing all 39 tool docs.
 * Uses jsPDF (already a dependency for other exporters) with a clean typographic layout.
 */
import jsPDF from "jspdf";
import { TOOL_DOCS } from "../docs/toolDocs";

const M = 18;            // page margin (mm)
const HEADER_H = 12;
const FOOTER_H = 10;

function newPage(doc, pageNum, totalPages, brand) {
  doc.setFillColor("#5A6B5D");
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), HEADER_H, "F");
  doc.setTextColor("#FFFFFF");
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text(brand, M, HEADER_H - 4);
  doc.setFont("helvetica", "normal");
  doc.text("Lean Toolbox · Consultant Handbook", doc.internal.pageSize.getWidth() - M, HEADER_H - 4, { align: "right" });

  // Footer
  const pageH = doc.internal.pageSize.getHeight();
  doc.setTextColor("#94A3A0");
  doc.setFontSize(8);
  doc.text(`Page ${pageNum}${totalPages ? ` of ${totalPages}` : ""}`, doc.internal.pageSize.getWidth() / 2, pageH - 4, { align: "center" });
  doc.setTextColor("#3F4D40");
}

function ensureSpace(doc, cursorY, needed, state) {
  const pageH = doc.internal.pageSize.getHeight();
  if (cursorY + needed > pageH - FOOTER_H - 4) {
    doc.addPage();
    state.page += 1;
    newPage(doc, state.page, null, state.brand);
    return HEADER_H + 8;
  }
  return cursorY;
}

function wrap(doc, text, maxW) {
  return doc.splitTextToSize(text || "", maxW);
}

function drawSection(doc, title, body, cursorY, state) {
  const W = doc.internal.pageSize.getWidth() - M * 2;
  cursorY = ensureSpace(doc, cursorY, 14, state);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor("#5A6B5D");
  doc.text(title.toUpperCase(), M, cursorY);
  cursorY += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor("#3F4D40");
  if (Array.isArray(body)) {
    body.forEach((line, i) => {
      const lines = wrap(doc, `${i + 1}. ${line}`, W - 6);
      cursorY = ensureSpace(doc, cursorY, lines.length * 5 + 1, state);
      doc.text(lines, M + 4, cursorY);
      cursorY += lines.length * 5 + 1;
    });
  } else {
    const lines = wrap(doc, body, W);
    cursorY = ensureSpace(doc, cursorY, lines.length * 5 + 2, state);
    doc.text(lines, M, cursorY);
    cursorY += lines.length * 5 + 2;
  }
  cursorY += 3;
  return cursorY;
}

function drawCover(doc, brand) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setFillColor("#3F4D40");
  doc.rect(0, 0, W, H, "F");
  doc.setTextColor("#FFFFFF");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(36);
  doc.text(brand, M, 90);
  doc.setFontSize(16);
  doc.setFont("helvetica", "normal");
  doc.text("Lean Toolbox — Consultant Handbook", M, 102);
  doc.setFontSize(11);
  doc.setTextColor("#C5D6C7");
  const intro =
    "A reference for all 39 wizards in this toolbox. Each entry covers " +
    "purpose, history, when to use, how to use, a worked example, " +
    "outputs and inferences, common pitfalls, and further reading.";
  doc.text(wrap(doc, intro, W - M * 2), M, 116);
  doc.setFontSize(9);
  doc.setTextColor("#9CA98D");
  doc.text(`Generated ${new Date().toLocaleDateString()} · ${TOOL_DOCS.length} tools`, M, H - 22);
}

function drawTOC(doc, state) {
  doc.addPage();
  state.page += 1;
  newPage(doc, state.page, null, state.brand);
  const W = doc.internal.pageSize.getWidth() - M * 2;
  let y = HEADER_H + 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor("#3F4D40");
  doc.text("Contents", M, y);
  y += 10;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  TOOL_DOCS.forEach((d, i) => {
    y = ensureSpace(doc, y, 6, state);
    doc.setTextColor("#3F4D40");
    doc.text(`${i + 1}. ${d.name}`, M, y);
    doc.setTextColor("#94A3A0");
    doc.text(d.category, M + W - 30, y);
    y += 5.5;
  });
}

function drawToolDoc(doc, d, idx, state) {
  doc.addPage();
  state.page += 1;
  newPage(doc, state.page, null, state.brand);

  const W = doc.internal.pageSize.getWidth() - M * 2;
  let y = HEADER_H + 12;

  // Title block
  doc.setFillColor("#E8F0E9");
  doc.rect(M, y - 6, W, 16, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor("#5A6B5D");
  doc.text(`${idx + 1} · ${d.category.toUpperCase()}`, M + 3, y);
  doc.setFontSize(15);
  doc.setTextColor("#3F4D40");
  doc.text(d.name, M + 3, y + 6);
  y += 16;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(9);
  doc.setTextColor("#5A6B5D");
  const tagL = wrap(doc, d.tagline, W);
  doc.text(tagL, M, y);
  y += tagL.length * 4.5 + 4;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor("#3F4D40");

  y = drawSection(doc, "Purpose", d.purpose, y, state);
  y = drawSection(doc, "History", d.history, y, state);
  y = drawSection(doc, "When to Use", d.whenToUse, y, state);
  y = drawSection(doc, "How to Use", d.howToUse, y, state);
  y = drawSection(doc, "Worked Example", d.example, y, state);
  y = drawSection(doc, "Outputs & Inferences", d.outputs, y, state);
  y = drawSection(doc, "Common Pitfalls", d.pitfalls, y, state);
  if (d.furtherReading?.length) {
    const refs = d.furtherReading.map(r => `${r.label} — ${r.ref}`);
    y = drawSection(doc, "Further Reading", refs, y, state);
  }
}

export function exportHandbookPDF({ firmName = "TheKaizenCabinet" } = {}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const state = { page: 1, brand: firmName };

  drawCover(doc, firmName);
  drawTOC(doc, state);
  TOOL_DOCS.forEach((d, i) => drawToolDoc(doc, d, i, state));

  doc.save(`${firmName.replace(/[^A-Za-z0-9]+/g, "")}-Consultant-Handbook.pdf`);
}
